import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  TableDownstreamVirtualConnectionsFilteredByColumnQuery,
  TableDownstreamVirtualConnectionsFilteredByColumnQueryVariables,
} from '../../../gen-src/queries/TableDownstreamVirtualConnectionsFilteredByColumnQuery';
import {
  TableDownstreamVirtualConnectionsQuery,
  TableDownstreamVirtualConnectionsQueryVariables,
} from '../../../gen-src/queries/TableDownstreamVirtualConnectionsQuery';
import {
  VirtualConnectionsDisplayGridRowDataFragment,
} from '../../../gen-src/queries/VirtualConnectionsDisplayGridRowDataFragment';
import { VirtualConnectionSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  TableDownstreamVirtualConnectionsFilteredByColumnQueryGql,
} from '../../queries/TableDownstreamVirtualConnectionsFilteredByColumnQueryGql';
import {
  TableDownstreamVirtualConnectionsQueryGql,
} from '../../queries/TableDownstreamVirtualConnectionsQueryGql';
import {
  getRowDataWithMaxCountsForVirtualConnection,
} from '../database/functions/VirtualConnectionRowDataFunctions';
import { VirtualConnectionsDisplayGridRowData } from '../database/types/VirtualConnectionDisplayGridRowData';
import { VirtualConnectionsDisplayGrid } from '../sharedDisplayGrid/VirtualConnectionsDisplayGrid';
import { VirtualConnectionsValidSortFields } from '../sorting/sortProviders/VirtualConnectionsGridSortProvider';


type QueryVariables = TableDownstreamVirtualConnectionsQueryVariables;
type QueryResults = TableDownstreamVirtualConnectionsQuery;
type QueryResultListNode = VirtualConnectionsDisplayGridRowDataFragment;
type GridRowData = VirtualConnectionsDisplayGridRowData;

type QueryVariablesWithFilter = TableDownstreamVirtualConnectionsFilteredByColumnQueryVariables;
type QueryResultsWithFilter = TableDownstreamVirtualConnectionsFilteredByColumnQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  return table.downstreamVirtualConnectionsConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNode> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  const column = table && table.columns && table.columns[0];
  return column.downstreamVirtualConnectionsConnection;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  const table = resultData && resultData.tables && resultData.tables[0];
  return table;
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData {
  const table = resultData && resultData.tables && resultData.tables[0];
  const column = table && table.columns && table.columns[0];
  return column;
}

function getRowDataForTable(
  node: QueryResultListNode,
  resultData: QueryResults,
): VirtualConnectionsDisplayGridRowData {
  return getRowDataWithMaxCountsForVirtualConnection(node, selectMaxCountsResult(resultData));
}

function getRowDataForTableForFilteredQuery(
  node: QueryResultListNode,
  resultData: QueryResultsWithFilter,
): VirtualConnectionsDisplayGridRowData {
  return getRowDataWithMaxCountsForVirtualConnection(node, selectMaxCountsResultForFilteredQuery(resultData));
}

export type TableDownstreamVirtualConnectionsQueryGridProps =
  GridSortProps<VirtualConnectionsValidSortFields> & {
    readonly tableId: string;
    readonly filteredColumnId: string | null;
    readonly rowCount: number | null;
  };

export function TableDownstreamVirtualConnectionsQueryGrid(props: TableDownstreamVirtualConnectionsQueryGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  const emptyGridMessage = Messages.EmptyDownstreamVirtualConnectionsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredTablesLineageGridMessage();

  return props.filteredColumnId ? (
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={TableDownstreamVirtualConnectionsFilteredByColumnQueryGql}
      partialQueryVariables={{
        tableId: props.tableId,
        columnId: props.filteredColumnId,
        orderBy: props.orderBy as VirtualConnectionSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForTableForFilteredQuery}
    >
      {({ rowStore }) =>
        <VirtualConnectionsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={TableDownstreamVirtualConnectionsQueryGql}
      partialQueryVariables={{
        tableId: props.tableId,
        orderBy: props.orderBy as VirtualConnectionSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForTable}
    >
      {({ rowStore }) =>
        <VirtualConnectionsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  );
}
